import { Chip, Box } from '@mui/material';
import { chipColors } from '../../lib/contrast';
import { useTranslation } from 'react-i18next';
import {
    SportsTennis,
    SportsSoccer,
    SportsBasketball,
    SportsVolleyball,
    SportsHandball,
    SportsGolf,
    SportsMartialArts,
    Pool,
    FitnessCenter,
    SelfImprovement,
    DirectionsRun,
    Waves,
    Stadium,
    SvgIconComponent,
} from '@mui/icons-material';

type Sport = {
    id: number;
    name?: string;
};

type Props = {
    sport: Sport;
};

type SportConfig = {
    icon: SvgIconComponent;
    color: string;
    name: string;
};

/**
 * Sport configuration with icons and colors
 */
const sportConfig: Record<number, SportConfig> = {
    1: { icon: SportsTennis, color: '#4CAF50', name: 'Tenis' }, // Green
    2: { icon: SportsTennis, color: '#2196F3', name: 'Padel' }, // Blue
    3: { icon: SportsSoccer, color: '#FF5722', name: 'Fútbol 5' }, // Deep Orange
    4: { icon: SportsSoccer, color: '#E91E63', name: 'Fútbol 7' }, // Pink
    5: { icon: SportsSoccer, color: '#9C27B0', name: 'Fútbol' }, // Purple
    6: { icon: SportsGolf, color: '#8BC34A', name: 'Golf' }, // Light Green
    28: { icon: Stadium, color: '#607D8B', name: 'Multiusos' }, // Blue Grey
    29: { icon: SportsBasketball, color: '#FF9800', name: 'Baloncesto' }, // Orange
    30: { icon: SportsHandball, color: '#795548', name: 'Balonmano' }, // Brown
    31: { icon: SportsTennis, color: '#00BCD4', name: 'Badminton' }, // Cyan
    32: { icon: SportsVolleyball, color: '#FFEB3B', name: 'Voleibol' }, // Yellow
    33: { icon: Waves, color: '#03A9F4', name: 'Calle piscina' }, // Light Blue
    34: { icon: Pool, color: '#0288D1', name: 'Piscina' }, // Dark Blue
    35: { icon: SportsTennis, color: '#673AB7', name: 'Squash' }, // Deep Purple
    38: { icon: SportsMartialArts, color: '#D32F2F', name: 'Karate' }, // Red Dark
    60: { icon: FitnessCenter, color: '#F44336', name: 'Pilates' }, // Red
    63: { icon: FitnessCenter, color: '#FF7043', name: 'Gimnasio' }, // Deep Orange Light
    67: { icon: DirectionsRun, color: '#5C6BC0', name: 'Atletismo' }, // Indigo
    70: { icon: Pool, color: '#00ACC1', name: 'Natación' }, // Cyan Dark
    72: { icon: SelfImprovement, color: '#7E57C2', name: 'Yoga' }, // Purple Light
    112: { icon: DirectionsRun, color: '#EC407A', name: 'Zumba' }, // Pink Light
    128: { icon: SportsTennis, color: '#26A69A', name: 'Pickleball' }, // Teal
};

/**
 * Default configuration for unknown sports
 */
const defaultConfig: SportConfig = {
    icon: DirectionsRun,
    color: '#9E9E9E', // Grey
    name: '',
};

export function SttChipSport({ sport }: Props): React.ReactElement {
    const { t } = useTranslation();

    const config = sportConfig[sport.id] || defaultConfig;
    const IconComponent = config.icon;

    // Try API translation first, fallback to config name or sport.name
    const translationKey = `sports.${sport.id}`;
    const translatedName = t(translationKey);
    // Check if translation exists (not equal to key and not starting with [[)
    const hasTranslation =
        translatedName &&
        translatedName !== translationKey &&
        !translatedName.startsWith('[[');
    const label = hasTranslation
        ? translatedName
        : config.name || sport.name || `Sport ${sport.id}`;

    return (
        <Chip
            size="small"
            icon={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 0.5,
                    }}
                >
                    <IconComponent
                        sx={{
                            fontSize: 16,
                            color: chipColors(config.color).color,
                        }}
                    />
                </Box>
            }
            label={label}
            sx={{
                ...chipColors(config.color),
                fontWeight: 500,
                '& .MuiChip-icon': {
                    color: chipColors(config.color).color,
                },
            }}
        />
    );
}
